import { getRouteForLocale, useI18nContext } from '@lemonade-hq/nuiza';
import type { AuthConfig } from '@lemonade-hq/nuiza';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

export interface NavContextValue {
  readonly auth: Pick<AuthConfig, 'loginUrl' | 'logoutUrl' | 'userInfoUrl'>;
  readonly nav: {
    readonly homeUrl: string;
    readonly monolithUrl: string;
    readonly consumerUrl: string;
    readonly privacyPolicyUrl: string;
  };
}

export const NavContext = createContext<NavContextValue>({
  auth: {
    loginUrl: '',
    logoutUrl: '',
    userInfoUrl: '',
  },
  nav: {
    homeUrl: '',
    monolithUrl: '',
    consumerUrl: '',
    privacyPolicyUrl: '',
  },
});

interface AuthUrlsProviderProps {
  readonly authConfig?: NavContextValue['auth'];
  readonly navConfig?: {
    readonly monolithUrl: string;
    readonly consumerUrl: string;
  };
}

export const NavProvider: React.FC<PropsWithChildren<AuthUrlsProviderProps>> = ({
  children,
  authConfig,
  navConfig,
}) => {
  const { locale } = useI18nContext();

  const value = useMemo(
    () =>
      ({
        auth: {
          loginUrl: authConfig?.loginUrl ?? '',
          logoutUrl: authConfig?.logoutUrl ?? '',
          userInfoUrl: authConfig?.userInfoUrl ?? '',
        },
        nav: {
          homeUrl: navConfig?.monolithUrl != null ? `${navConfig.monolithUrl}${getRouteForLocale('home', locale)}` : '',
          monolithUrl: navConfig?.monolithUrl ?? '',
          consumerUrl: navConfig?.consumerUrl ?? '',
          privacyPolicyUrl: `${navConfig?.monolithUrl}${getRouteForLocale('privacy_policy', locale)}`,
        },
      }) as const,
    [authConfig, navConfig, locale],
  );
  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};

export function useNavContext(): NavContextValue {
  return useContext(NavContext);
}
